/**
 * Create a preset from a spreadsheet ("Create from list").
 *
 * Three steps in one modal: choose a file and nominate the sheet / column /
 * identifier type / column role, match against the API, then reconcile and
 * commit. The workbook is parsed IN THE BROWSER and never uploaded - only
 * the nominated column's values go to the resolve endpoint. That is a
 * deliberate data-safety property (the registers people import can be
 * highly sensitive); do not add a file-upload path.
 *
 * Reconciliation rules (pinned by the tests):
 *   - Ambiguous rows BLOCK commit: an undecided ambiguous row would silently
 *     resolve to nobody.
 *   - Unmatched rows do NOT block: a row the admin chooses not to replace is
 *     a deliberate drop.
 *   - Both the row count and the unique contact count are reported - siblings
 *     routinely share carers, so the two legitimately differ.
 *   - Exceeding the individual cap warns and blocks commit rather than
 *     throwing the reconciliation session away with a 422.
 *
 * A match report is only valid for the exact inputs that produced it, so any
 * change to the file, sheet, column, identifier type, role or carer toggle
 * invalidates it (and a generation counter drops in-flight results that
 * arrive after an invalidation - see invalidateReport).
 *
 * Commit goes through the EXISTING useCreatePresetAdmin with
 * show_member_list: false - imported lists default to a hidden member list.
 */
import { useMemo, useRef, useState } from "react";
import {
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToggle,
  IonToolbar,
} from "@ionic/react";
import {
  columnLabel,
  extractColumn,
  parseWorkbook,
  type SheetSummary,
} from "../../lib/workbookColumns";
import { useResolveContacts } from "../../hooks/useResolveContacts";
import { useCreatePresetAdmin } from "../../hooks/usePresets";
import { useMyDetails } from "../../hooks/useMyDetails";
import { ContactPicker } from "./ContactPicker";
import { parsePresetError } from "./presetErrorParser";
import {
  MAX_SUBMITTED_VALUES,
  MAX_VALUE_LENGTHS,
  type ColumnRole,
  type IdentifierType,
  type ResolveContactsResponse,
} from "./importTypes";

import "./presets.css";

interface ImportPresetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const IDENTIFIER_OPTIONS: Array<{ value: IdentifierType; label: string }> = [
  { value: "email", label: "Email address" },
  { value: "edumate_contact_id", label: "Edumate contact id" },
  { value: "student_number", label: "Student number" },
];

const ROLE_OPTIONS: Array<{ value: ColumnRole; label: string }> = [
  { value: "contact", label: "The person to add" },
  { value: "student", label: "A student - add their carers instead" },
];

export function ImportPresetModal({ isOpen, onClose }: ImportPresetModalProps) {
  const [buffer, setBuffer] = useState<ArrayBuffer | null>(null);
  const [fileName, setFileName] = useState("");
  const [sheets, setSheets] = useState<SheetSummary[]>([]);
  const [sheetName, setSheetName] = useState("");
  const [columnNumber, setColumnNumber] = useState<number | null>(null);
  const [identifierType, setIdentifierType] = useState<IdentifierType>("email");
  const [role, setRole] = useState<ColumnRole>("contact");
  const [mailCarersOnly, setMailCarersOnly] = useState(true);
  const [report, setReport] = useState<ResolveContactsResponse | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);
  const [name, setName] = useState("");
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorBullets, setErrorBullets] = useState<string[]>([]);

  // value -> chosen contact_id, for both ambiguous picks and unmatched
  // replacements. Absent means still undecided (ambiguous) or dropped
  // (unmatched).
  const [decisions, setDecisions] = useState<Record<string, string>>({});
  // The unmatched value whose replacement picker is currently open.
  const [replaceOpenFor, setReplaceOpenFor] = useState<string | null>(null);

  // Bumped by every invalidation; in-flight async work (a workbook parse or
  // a match request) compares its captured generation on completion and
  // discards itself if anything changed while it was away (new file, changed
  // options, dismissed modal). Without this, a slow response can resurrect
  // state for inputs no longer selected.
  const sessionGeneration = useRef(0);

  const resolve = useResolveContacts();
  const create = useCreatePresetAdmin();
  const { user } = useMyDetails();

  // The admin importing the list becomes the preset's owner. `contact_id`
  // is the established /my/details/ field (PresetDetail's owner check reads
  // it); `foreign_keys.edumate_contact_id` covers the auth-side shape and
  // `SmsId` (stringified) the legacy portal-style fallback. All three carry
  // the same numeric Edumate id.
  const ownerContactId = useMemo(() => {
    const raw =
      user?.contact_id ?? user?.foreign_keys?.edumate_contact_id ?? user?.SmsId;
    const numeric = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }, [user]);

  const clearError = () => {
    setErrorTitle(null);
    setErrorBullets([]);
  };

  /** A report is only valid for the inputs that produced it. Call on EVERY
   *  input change; also drops any in-flight match via the generation. */
  const invalidateReport = () => {
    sessionGeneration.current += 1;
    setReport(null);
    setSkippedCount(0);
    setDecisions({});
    setReplaceOpenFor(null);
  };

  const resetAll = () => {
    invalidateReport();
    setBuffer(null);
    setFileName("");
    setSheets([]);
    setSheetName("");
    setColumnNumber(null);
    setIdentifierType("email");
    setRole("contact");
    setMailCarersOnly(true);
    setName("");
    clearError();
  };

  const closeAndReset = () => {
    resetAll();
    onClose();
  };

  const onFileChange = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    clearError();
    invalidateReport();
    // Parsing is async: if another file is chosen (or the modal dismissed)
    // before this one finishes, the stale parse must not overwrite state.
    const generation = sessionGeneration.current;
    try {
      const data = await file.arrayBuffer();
      const parsed = await parseWorkbook(data);
      if (generation !== sessionGeneration.current) return;
      setBuffer(data);
      setFileName(file.name);
      setSheets(parsed);
      setSheetName(parsed[0]?.name ?? "");
      setColumnNumber(parsed[0]?.columns[0]?.columnNumber ?? null);
    } catch {
      if (generation !== sessionGeneration.current) return;
      // Counts/categories only - never echo file contents into errors.
      setBuffer(null);
      setFileName("");
      setSheets([]);
      setSheetName("");
      setColumnNumber(null);
      setErrorTitle("Could not read that file - is it a valid .xlsx workbook?");
    }
  };

  const onSheetChange = (nextSheet: string) => {
    invalidateReport();
    setSheetName(nextSheet);
    const sheet = sheets.find((s) => s.name === nextSheet);
    setColumnNumber(sheet?.columns[0]?.columnNumber ?? null);
  };

  const onMatch = async () => {
    if (!buffer || !sheetName || columnNumber === null) return;
    clearError();
    invalidateReport();
    const generation = sessionGeneration.current;
    try {
      const values = await extractColumn(buffer, sheetName, columnNumber);
      // Mirror the server's per-item cap client-side: an over-long string can
      // never be a valid identifier, and sending one 422s the whole request.
      const cap = MAX_VALUE_LENGTHS[identifierType];
      const sendable = values.filter((v) => v.length <= cap);

      if (sendable.length === 0) {
        setErrorTitle(
          values.length === 0
            ? "No values found in that column"
            : "No usable values in that column - every value exceeds the " +
                "length cap for this identifier type"
        );
        return;
      }
      if (sendable.length > MAX_SUBMITTED_VALUES) {
        setErrorTitle(
          `That column has ${sendable.length} values - the limit per import ` +
            `is ${MAX_SUBMITTED_VALUES}. Split the list and import in parts.`
        );
        return;
      }

      const result = await resolve.mutateAsync({
        identifier_type: identifierType,
        role,
        values: sendable,
        mail_carers_only: mailCarersOnly,
      });
      if (generation !== sessionGeneration.current) return;
      setSkippedCount(values.length - sendable.length);
      setReport(result);
    } catch (err) {
      if (generation !== sessionGeneration.current) return;
      const parsed = parsePresetError(err);
      if (parsed) {
        setErrorTitle(parsed.title);
        setErrorBullets(parsed.bullets);
      } else {
        setErrorTitle("Matching failed - please try again");
      }
    }
  };

  const setDecision = (value: string, contactId: string | null) => {
    setDecisions((prev) => {
      const next = { ...prev };
      if (contactId) {
        next[value] = contactId;
      } else {
        delete next[value];
      }
      return next;
    });
  };

  // Auto-resolved rows, plus anything the admin decided by hand. A student
  // row can arrive as SEVERAL resolved entries sharing one value (one per
  // carer, apis#1117) - collecting every entry's contact_id is what commits
  // them all. Deduped: siblings routinely converge on the same carer.
  const acceptedIds = useMemo(() => {
    if (!report) return [];
    const ids = report.resolved.map((r) => r.contact_id);
    const decided = Object.values(decisions).filter(Boolean);
    return Array.from(new Set([...ids, ...decided]));
  }, [report, decisions]);

  const unresolvedAmbiguous = useMemo(
    () => (report?.ambiguous ?? []).filter((a) => !decisions[a.value]),
    [report, decisions]
  );

  // Server-computed flag OR the post-reconciliation total (replacements can
  // push a borderline list over the cap after the report came back under it).
  const overCap =
    !!report &&
    (report.exceeds_individual_cap || acceptedIds.length > report.max_individuals);

  // Unmatched rows do NOT block: dropping a row the admin cannot place is a
  // legitimate decision. Ambiguous rows DO block, because leaving one
  // undecided silently picks nobody.
  const canCommit =
    !!report &&
    name.trim().length > 0 &&
    unresolvedAmbiguous.length === 0 &&
    !overCap &&
    acceptedIds.length > 0 &&
    ownerContactId !== null &&
    !create.isPending;

  const onCreate = async () => {
    if (!report || ownerContactId === null) return;
    clearError();
    // If the modal is dismissed while the create is in flight, the session
    // was reset: neither close again (duplicate onClose) nor restore an
    // error into the fresh state. The server-side create itself cannot be
    // recalled once sent - backdropDismiss is disabled while pending to
    // keep that window closed.
    const generation = sessionGeneration.current;
    try {
      await create.mutateAsync({
        owner_contact_id: ownerContactId,
        name_short: name.trim(),
        name_long: name.trim(),
        audience: "shared",
        manager_contact_ids: [],
        // Imported lists default to a hidden member list - the register a
        // list came from may be sensitive, so visibility is opt-in later.
        show_member_list: false,
        definition: {
          constituent_group_ids: [],
          individual_contact_ids: acceptedIds.map(
            (id) => `ContactPersonal/${id}`
          ),
        },
      });
      if (generation !== sessionGeneration.current) return;
      closeAndReset();
    } catch (err) {
      if (generation !== sessionGeneration.current) return;
      const parsed = parsePresetError(err);
      if (parsed) {
        setErrorTitle(parsed.title);
        setErrorBullets(parsed.bullets);
      } else {
        setErrorTitle("Failed to create preset - please try again");
      }
    }
  };

  const selectedSheet = sheets.find((s) => s.name === sheetName);

  // Duplicate header names are real (exports repeat "Email" etc.), so a
  // duplicate gets its spreadsheet column letter appended for disambiguation.
  const columnOptions = useMemo(() => {
    const columns = selectedSheet?.columns ?? [];
    const counts = new Map<string, number>();
    columns.forEach((c) => {
      counts.set(c.header, (counts.get(c.header) ?? 0) + 1);
    });
    return columns.map((c) => ({
      columnNumber: c.columnNumber,
      label:
        (counts.get(c.header) ?? 0) > 1
          ? `${c.header} (column ${columnLabel(c.columnNumber)})`
          : c.header,
    }));
  }, [selectedSheet]);

  // The unique-contact count is derived from the SAME state onCreate commits
  // (acceptedIds), never from the match-time server count: ambiguity
  // decisions and unmatched replacements made after the match must move this
  // number, or it reads stale - "0 unique contacts" after every row was
  // decided, while Create preset correctly committed them (gm#132).
  const summaryLine = report
    ? `${report.submitted_count} rows resolved to ` +
      `${acceptedIds.length} unique contact` +
      `${acceptedIds.length === 1 ? "" : "s"}`
    : "";
  const decisionLine =
    unresolvedAmbiguous.length > 0
      ? `${unresolvedAmbiguous.length} need` +
        `${unresolvedAmbiguous.length === 1 ? "s" : ""} a decision`
      : "";
  const capLine =
    report && overCap
      ? `This list has ${acceptedIds.length} contacts, which exceeds the ` +
        `limit of ${report.max_individuals}. Trim the list and re-import.`
      : "";

  return (
    <IonModal
      isOpen={isOpen}
      // Ionic's controlled isOpen is one-way: without this, a backdrop or
      // ESC dismiss leaves the parent stuck at open=true.
      onDidDismiss={closeAndReset}
      // A create cannot be recalled once sent, so a backdrop/ESC dismiss
      // mid-create would only LOOK like a cancel. Block it while pending
      // (programmatic isOpen=false dismissal is unaffected).
      backdropDismiss={!create.isPending}
      className="preset-import-modal"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Create preset from a list</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        {errorTitle && (
          <div className="preset-modal-error" role="alert">
            <strong>{errorTitle}</strong>
            {errorBullets.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {errorBullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="preset-modal-field">
          <span className="preset-modal-field-label">Spreadsheet</span>
          <span className="preset-modal-field-hint">
            The file is read in your browser and never uploaded - only the
            values of the column you nominate are sent for matching.
          </span>
          <input
            type="file"
            aria-label="Spreadsheet file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => onFileChange(e.target.files)}
          />
          {fileName && <IonNote>{fileName}</IonNote>}
        </div>

        {sheets.length > 0 && (
          <>
            <IonList lines="none">
              <IonItem>
                <IonLabel>Sheet</IonLabel>
                <IonSelect
                  aria-label="Sheet"
                  value={sheetName}
                  onIonChange={(e) => onSheetChange(String(e.detail.value))}
                >
                  {sheets.map((s) => (
                    <IonSelectOption key={s.name} value={s.name}>
                      {s.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              {selectedSheet && (
                <IonItem>
                  <IonNote>
                    {`${selectedSheet.rowCount} data row` +
                      `${selectedSheet.rowCount === 1 ? "" : "s"}`}
                  </IonNote>
                </IonItem>
              )}
              <IonItem>
                <IonLabel>Column</IonLabel>
                <IonSelect
                  aria-label="Column"
                  value={columnNumber}
                  onIonChange={(e) => {
                    invalidateReport();
                    const next = Number(e.detail.value);
                    setColumnNumber(Number.isFinite(next) ? next : null);
                  }}
                >
                  {columnOptions.map((c) => (
                    <IonSelectOption
                      key={c.columnNumber}
                      value={c.columnNumber}
                    >
                      {c.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonLabel>Identifier type</IonLabel>
                <IonSelect
                  aria-label="Identifier type"
                  value={identifierType}
                  onIonChange={(e) => {
                    invalidateReport();
                    setIdentifierType(e.detail.value as IdentifierType);
                  }}
                >
                  {IDENTIFIER_OPTIONS.map((o) => (
                    <IonSelectOption key={o.value} value={o.value}>
                      {o.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonLabel>The column holds</IonLabel>
                <IonSelect
                  aria-label="Column role"
                  value={role}
                  onIonChange={(e) => {
                    invalidateReport();
                    setRole(e.detail.value as ColumnRole);
                  }}
                >
                  {ROLE_OPTIONS.map((o) => (
                    <IonSelectOption key={o.value} value={o.value}>
                      {o.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              {role === "student" && (
                <IonItem>
                  <IonLabel>Mail carers only</IonLabel>
                  <IonToggle
                    aria-label="Mail carers only"
                    checked={mailCarersOnly}
                    onIonChange={(e) => {
                      invalidateReport();
                      setMailCarersOnly(e.detail.checked);
                    }}
                  />
                </IonItem>
              )}
            </IonList>

            <IonButton
              expand="block"
              color="secondary"
              onClick={onMatch}
              disabled={resolve.isPending || columnNumber === null}
            >
              {resolve.isPending ? "Matching..." : "Match contacts"}
            </IonButton>
          </>
        )}

        {report && (
          <div className="preset-import-report">
            <h2 className="preset-section-title">Match report</h2>
            <p>{summaryLine}</p>
            {skippedCount > 0 && (
              <IonNote>
                {`${skippedCount} value` +
                  `${skippedCount === 1 ? " was" : "s were"} too long to be ` +
                  `a valid identifier and ${skippedCount === 1 ? "was" : "were"} skipped.`}
              </IonNote>
            )}
            {capLine && (
              <div className="preset-modal-error" role="alert">
                {capLine}
              </div>
            )}

            {report.ambiguous.length > 0 && (
              <div className="preset-import-bucket">
                <h3>
                  {`Ambiguous (${report.ambiguous.length})` +
                    (decisionLine ? ` - ${decisionLine}` : "")}
                </h3>
                <IonNote>
                  These values matched more than one person. Pick who was
                  meant - the preset cannot be created while any are
                  undecided.
                </IonNote>
                <IonList lines="none">
                  {report.ambiguous.map((row) => (
                    <IonItem key={row.value}>
                      <IonLabel>{row.value}</IonLabel>
                      <IonSelect
                        aria-label={`Choose a contact for ${row.value}`}
                        placeholder="Choose..."
                        value={decisions[row.value] ?? null}
                        onIonChange={(e) =>
                          setDecision(
                            row.value,
                            e.detail.value ? String(e.detail.value) : null
                          )
                        }
                      >
                        {row.candidates.map((c) => (
                          <IonSelectOption
                            key={c.contact_id}
                            value={c.contact_id}
                          >
                            {c.label}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            {report.inactive.length > 0 && (
              <div className="preset-import-bucket">
                <h3>{`No longer active (${report.inactive.length})`}</h3>
                <IonNote>
                  These matched a contact who has left. They are not added -
                  use Replace on an unmatched row if someone else was meant.
                </IonNote>
                <IonList lines="none">
                  {report.inactive.map((row) => (
                    <IonItem key={row.value}>
                      <IonLabel>
                        {row.value} - {row.label}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </div>
            )}

            {report.unmatched.length > 0 && (
              <div className="preset-import-bucket">
                <h3>{`Unmatched (${report.unmatched.length})`}</h3>
                <IonNote>
                  These matched nobody. Replace a row with a searched contact,
                  or leave it to drop it from the preset.
                </IonNote>
                <IonList lines="none">
                  {report.unmatched.map((value) => (
                    <IonItem key={value}>
                      <IonLabel>
                        {value}
                        {decisions[value] ? " - replacement chosen" : ""}
                      </IonLabel>
                      <button
                        type="button"
                        className="preset-import-replace"
                        aria-label={`Find a replacement for ${value}`}
                        onClick={() =>
                          setReplaceOpenFor((open) =>
                            open === value ? null : value
                          )
                        }
                      >
                        Replace
                      </button>
                    </IonItem>
                  ))}
                </IonList>
                {replaceOpenFor !== null && (
                  <ContactPicker
                    mode="prefixed"
                    collapsible
                    countNoun="contact"
                    selectedIds={
                      decisions[replaceOpenFor]
                        ? [`ContactPersonal/${decisions[replaceOpenFor]}`]
                        : []
                    }
                    onChange={(ids) => {
                      // Single-select: keep only the last pick. The picker
                      // emits "ContactPersonal/<id>"; decisions hold bare ids.
                      const last = ids[ids.length - 1];
                      setDecision(
                        replaceOpenFor,
                        last ? last.split("/")[1] ?? null : null
                      );
                    }}
                  />
                )}
              </div>
            )}

            <div className="preset-modal-field">
              <span className="preset-modal-field-label">Preset name</span>
              <IonInput
                className="preset-text-input"
                fill="outline"
                aria-label="Preset name"
                value={name}
                onIonInput={(e) => setName(e.detail.value ?? "")}
                placeholder="e.g. Term 3 excursion list"
                maxlength={80}
              />
            </div>
          </div>
        )}
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <div className="preset-modal-actions">
            <IonButton
              fill="outline"
              onClick={closeAndReset}
              disabled={create.isPending}
            >
              Cancel
            </IonButton>
            <IonButton
              color="secondary"
              onClick={onCreate}
              disabled={!canCommit}
            >
              {create.isPending ? "Creating..." : "Create preset"}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
}
